/**
 * Kleur van de hover-highlightranden (de kaders rond planeetblokken, de ringen
 * rond poortcirkels, center-randen, mandala-outlines, ...). App-setting onder
 * "Dimmen bij hover"; zie `docs/human-design/hd-chart-hover-highlight.md` §11.
 *
 * - `blue`: het klassieke blauw. Zet géén CSS-variabelen, zodat elke usage
 *   terugvalt op zijn eigen, ongewijzigde blauw en zijn huidige maten.
 * - `green`: fel groen met beduidend meer contrast, en dikkere planeet-outlines
 *   (3px, zonder offset) om het verschil met blauw te kunnen vergelijken.
 */
export type HdHoverHighlightColor = 'blue' | 'green';

export const HD_HOVER_HIGHLIGHT_COLORS: readonly HdHoverHighlightColor[] = ['blue', 'green'];

export const HD_HOVER_HIGHLIGHT_COLOR_DEFAULT: HdHoverHighlightColor = 'green';

/**
 * De CSS custom properties die de hover-usages lezen, telkens met hun
 * blauwe waarde als fallback: `var(--hd-hover-color, #218df0)`,
 * `var(--hd-hover-outline-width, 2px)`, `var(--hd-hover-outline-offset, 1px)`.
 * De chart-SVG valt via `$hd-hover-color` in `_hd-bodygraph-tokens.scss` terug
 * op zijn eigen (iets lichtere) highlight-blauw.
 */
const HD_HOVER_HIGHLIGHT_THEME_VARS: Readonly<Record<HdHoverHighlightColor, Readonly<Record<string, string>>>> = {
  blue: {},
  green: {
    '--hd-hover-color': '#56ff00',
    '--hd-hover-outline-width': '3px',
    '--hd-hover-outline-offset': '0px',
  },
};

const HD_HOVER_HIGHLIGHT_THEME_PROPERTIES = ['--hd-hover-color', '--hd-hover-outline-width', '--hd-hover-outline-offset'];

export function coerceHdHoverHighlightColor(value: unknown): HdHoverHighlightColor {
  return HD_HOVER_HIGHLIGHT_COLORS.includes(value as HdHoverHighlightColor)
    ? value as HdHoverHighlightColor
    : HD_HOVER_HIGHLIGHT_COLOR_DEFAULT;
}

/**
 * Zet (of wist) de thema-variabelen op een style-declaratie — typisch die van
 * `document.documentElement`, zodat ook CDK-overlays buiten de pagina-host
 * dezelfde kleur volgen.
 */
export function applyHdHoverHighlightTheme(style: CSSStyleDeclaration, color: HdHoverHighlightColor | null): void {
  const vars = color == null ? {} : HD_HOVER_HIGHLIGHT_THEME_VARS[color];
  for (const property of HD_HOVER_HIGHLIGHT_THEME_PROPERTIES) {
    const value = vars[property];
    if (value == null) {
      style.removeProperty(property);
    } else {
      style.setProperty(property, value);
    }
  }
}
